/**
 * Shared cell renderers for DataGrid.
 *
 * These are reusable across any entity type (legislators, candidates, etc.)
 * Each renderer accesses entity data via params.data and callbacks via params.context.
 */

import React from 'react';
import {
  Mail,
  Phone,
  Globe,
  Star,
  Share2,
  Users,
  StickyNote,
  FileText,
} from 'lucide-react';
import { type ICellRendererParams } from 'ag-grid-community';
import Chip from '../Chip';
import DistrictMapLink from '../DistrictMapLink';
import { prefetchHostedDistrictMapUrls } from '../../utils/districtMapUrls';
import { WorkspaceBadgeList } from '../WorkspaceBadge';
import CustomFieldBadge from '../custom-fields/CustomFieldBadge';
import { getPartyTextColor, getPartyChipVariant, formatPartyDisplay } from '../../utils/partyUtils';
import { getPositionColorClasses } from '../../utils/positionColors';
import { ensureHttpsUrl } from '../candidates/candidateContactUtils';
import { formatPhoneNumber, formatPersonName } from '../../utils/nameUtils';
import { padDistrictNumber } from '../../utils/districtDisplay';
import { incomingLabel } from '../legislators/incomingRoster';
import { formatDate } from '../../utils/dateUtils';

// =============================================================================
// Name Cell - clickable name with optional priority star and incumbent badge
// =============================================================================

export const NameCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const ctx = props.context || {};

  const name = formatPersonName(row);
  const party = row.partyAffiliation || row.party_affiliation || '';
  const isPriority = row.isPriority || row.is_priority;
  const isIncumbent = row.isIncumbent || row.is_incumbent;
  const incoming = incomingLabel(row);
  const partyColor = getPartyTextColor(party);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    ctx.onNavigate?.(row.id);
  };

  return (
    <div className="flex items-center gap-2 whitespace-nowrap py-1">
      <button
        onClick={handleClick}
        className={`font-medium hover:underline cursor-pointer text-sm ${partyColor}`}
      >
        {name}
      </button>
      {isPriority && (
        <Star className="w-3.5 h-3.5 text-yellow-500 fill-current flex-shrink-0" />
      )}
      {isIncumbent && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-info-100 text-info-800 dark:bg-info-900/20 dark:text-info-400">
          Inc.
        </span>
      )}
      {incoming && <Chip label={incoming} tone={incoming === 'Continuing' ? 'neutral' : 'positive'} size="sm" />}
      {incoming && row.takesOffice && (
        <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">takes office {formatDate(row.takesOffice)}</span>
      )}
    </div>
  );
};

// =============================================================================
// Party Cell - party chip or text
// =============================================================================

export const PartyCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const party = row.partyAffiliation || row.party_affiliation || '';
  if (!party) return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;
  return <Chip label={formatPartyDisplay(party)} variant={getPartyChipVariant(party)} size="sm" />;
};

// =============================================================================
// Chamber Cell - House/Senate chip
// =============================================================================

export const ChamberCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const chamber = row.chamber || '';
  if (!chamber) return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;
  return <Chip label={chamber} variant={chamber === 'House' ? 'primary' : 'secondary'} size="sm" />;
};

// =============================================================================
// District Cell - links to district map when chamber is available
// =============================================================================

function deriveChamber(office: string): string {
  if (!office) return '';
  const upper = office.toUpperCase();
  if (upper.includes('HOUSE')) return 'House';
  if (upper.includes('SENATE')) return 'Senate';
  return '';
}

function extractDistrictNumber(district: string): string {
  const str = String(district);
  const match = str.match(/(\d+)/);
  return match ? match[1].replace(/^0+/, '') || match[1] : str;
}

/**
 * Batch-resolve hosted district maps for a set of grid rows so the per-row
 * DistrictMapLink mounts read from cache instead of each firing their own
 * GET /district-maps. Uses the same chamber/district derivation as
 * DistrictCellRenderer. Safe (and cheap) to call after row data loads.
 */
export function prefetchDistrictMapsForRows(
  rows: ReadonlyArray<unknown> | undefined | null
): void {
  if (!Array.isArray(rows) || rows.length === 0) return;
  const items: Array<{ chamber: string; district: string }> = [];
  for (const raw of rows) {
    if (!raw || typeof raw !== 'object') continue;
    const row = raw as Record<string, unknown>;
    const rawDistrict = row['district'] != null ? String(row['district']) : '';
    if (!rawDistrict) continue;
    const chamber =
      (row['chamber'] as string) ||
      deriveChamber((row['officeSought'] as string) || (row['office_sought'] as string) || '');
    if (!chamber) continue;
    items.push({ chamber, district: extractDistrictNumber(rawDistrict) });
  }
  if (items.length > 0) {
    void prefetchHostedDistrictMapUrls(items);
  }
}

export const DistrictCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const rawDistrict = row.district != null ? String(row.district) : '';
  if (!rawDistrict) {
    return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;
  }

  const chamber = row.chamber || deriveChamber(row.officeSought || row.office_sought || '');
  const displayDistrict = extractDistrictNumber(rawDistrict);

  if (chamber) {
    return (
      <DistrictMapLink
        chamber={chamber}
        district={displayDistrict}
        className="text-sm text-text-secondary dark:text-text-dark-secondary"
      >
        {padDistrictNumber(displayDistrict)}
      </DistrictMapLink>
    );
  }

  return (
    <span className="text-sm text-text-secondary dark:text-text-dark-secondary whitespace-nowrap">
      {padDistrictNumber(displayDistrict)}
    </span>
  );
};

// =============================================================================
// Contact Cell - email/phone/website links
// =============================================================================

export const ContactCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const email = row.email || '';
  const phone = row.workPhone || row.work_phone || row.phone || '';

  if (!email && !phone) {
    return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;
  }

  return (
    <div className="flex flex-col gap-1 py-1">
      {email && (
        <a
          href={`mailto:${email}`}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
          title={email}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); window.location.href = `mailto:${email}`; }}
        >
          <Mail className="w-3 h-3 flex-shrink-0" />
          <span className="truncate max-w-[140px]">{email}</span>
        </a>
      )}
      {phone && (
        <a
          href={`tel:${phone}`}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
          title={formatPhoneNumber(phone)}
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); window.location.href = `tel:${phone}`; }}
        >
          <Phone className="w-3 h-3 flex-shrink-0" />
          <span>{formatPhoneNumber(phone)}</span>
        </a>
      )}
    </div>
  );
};

// =============================================================================
// Position Cell - dropdown (editable) or badge (read-only)
// =============================================================================

export const PositionCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const ctx = props.context || {};
  const position = row.position || 'Neutral';
  /*
    canManage, not canEdit: a position is the organisation speaking, so only a
    workspace manager may set one (2026-08-18). A contributor still SEES it —
    they cannot work to a stance they cannot read — which is what the
    read-only branch below already did for viewers and report mode.
  */
  const canSetPosition = ctx.permissions?.canManage;

  if (canSetPosition) {
    return (
      <select
        value={position}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          e.stopPropagation();
          ctx.onPositionChange?.(row.id, e.target.value);
        }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        className={`text-xs px-2 py-1 border border-border-light dark:border-border-dark rounded-md font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${getPositionColorClasses(position)}`}
      >
        <option value="Support">Support</option>
        <option value="Oppose">Oppose</option>
        <option value="Monitor">Monitor</option>
        <option value="Neutral">Neutral</option>
      </select>
    );
  }

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${getPositionColorClasses(position)}`}>
      {position}
    </span>
  );
};

// =============================================================================
// Notes Cell - renders a notes badge/icon via context callback
// =============================================================================

export const NotesCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const ctx = props.context || {};

  if (ctx.renderNotesCell) {
    return <>{ctx.renderNotesCell(row)}</>;
  }

  return (
    <div className="flex items-center justify-center">
      <StickyNote className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
    </div>
  );
};

// =============================================================================
// Workspace Badges Cell - for org-mode views
// =============================================================================

export const WorkspacesCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const workspaces = row.workspaces as Array<{ id: string; name: string; position?: string }> | undefined;
  if (!workspaces || workspaces.length === 0) {
    return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;
  }
  return <WorkspaceBadgeList workspaces={workspaces} />;
};

// =============================================================================
// Custom Fields Cell
// =============================================================================

export const CustomFieldsCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const ctx = props.context || {};
  if (!ctx.workspaceId) {
    return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;
  }

  const stopAll = (e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault(); };

  return (
    <div data-no-row-click onClick={stopAll} onMouseDown={(e) => e.stopPropagation()}>
      <CustomFieldBadge
        entityType={ctx.entityType || 'candidate'}
        entityId={row.id}
        entityDisplayName={ctx.getEntityDisplayName?.(row) ?? (row.fullName || row.full_name || '')}
        workspaceId={ctx.workspaceId}
        count={row.customFieldCount}
        canEdit={ctx.permissions?.canEdit}
      />
    </div>
  );
};

// =============================================================================
// Committees Cell - text list
// =============================================================================

export const CommitteesCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const committees = row.committees || '';
  return (
    <div className="break-words text-sm text-text-secondary dark:text-text-dark-secondary line-clamp-2 py-1">
      {committees || 'None'}
    </div>
  );
};

// =============================================================================
// Sponsored Bills Cell - clickable count linking to filtered bills
// =============================================================================

export const SponsoredBillsCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const ctx = props.context || {};
  const count = row.sponsoredBillsCount ?? row.sponsored_bills_count ?? 0;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    ctx.onNavigateBills?.(row);
  };

  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-1 text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
    >
      <FileText className="w-4 h-4" />
      <span className="font-medium">{count}</span>
    </button>
  );
};

// =============================================================================
// Funds Cell - compact currency display
// =============================================================================

function formatCompactCurrency(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return `$${amount.toLocaleString()}`;
}

export const FundsCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const amount = row.totalFundsRaised || row.total_funds_raised;
  if (!amount || amount <= 0) {
    return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;
  }
  return (
    <span className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary whitespace-nowrap">
      {formatCompactCurrency(amount)}
    </span>
  );
};

// =============================================================================
// Status Cell - chip with variant
// =============================================================================

interface StatusCellContext {
  statusVariant?: (status: string) => 'success' | 'warning' | 'danger' | 'info' | 'neutral';
}

export const StatusCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const ctx = (props.context || {}) as StatusCellContext;
  const status = row.status || '';
  if (!status) return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;

  const label = status.charAt(0).toUpperCase() + status.slice(1);
  const variant = ctx.statusVariant?.(status) || 'neutral';
  return <Chip label={label} variant={variant} size="sm" />;
};

// =============================================================================
// Seat Incumbent Cell
// =============================================================================

export const IncumbentCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const ctx = props.context || {};
  const name = row.seatIncumbentName || row.seat_incumbent_name;
  const id = row.seatIncumbentId || row.seat_incumbent_id;
  if (!name) return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!id) return;
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(id));
    if (isUUID) {
      ctx.onNavigateCandidate?.(id);
    } else {
      ctx.onNavigateLegislator?.(id);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`text-sm font-medium whitespace-nowrap py-1 ${id ? 'text-primary-600 dark:text-primary-400 hover:underline cursor-pointer' : 'text-text-secondary dark:text-text-dark-secondary'}`}
    >
      {name}
    </button>
  );
};

// =============================================================================
// Election Year Cell
// =============================================================================

export const YearCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  return (
    <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
      {row.electionYear || row.election_year || '—'}
    </span>
  );
};

// =============================================================================
// Office Cell
// =============================================================================

export const OfficeCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  return (
    <span className="text-sm text-text-secondary dark:text-text-dark-secondary whitespace-nowrap">
      {row.officeSought || row.office_sought || '—'}
    </span>
  );
};

// =============================================================================
// Web/Social Cell - website + social media links
// =============================================================================

const SOCIAL_LABEL: Record<string, string> = {
  x: 'X', instagram: 'IG', facebook: 'FB', linkedin: 'LI', bluesky: 'BS', youtube: 'YT',
};

export const WebSocialCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;

  const website = row.website || '';
  const socialMedia = row.socialMedia || [];
  if (!website && socialMedia.length === 0) {
    return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;
  }

  const webUrl = website ? ensureHttpsUrl(website) : '';

  return (
    <div className="flex flex-col gap-1 py-1">
      {website && (
        <a
          href={webUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
          title={website}
          onClick={(e) => e.stopPropagation()}
        >
          <Globe className="w-3 h-3 flex-shrink-0" />
          <span className="truncate max-w-[140px]">{website.replace(/^https?:\/\//, '')}</span>
        </a>
      )}
      {socialMedia.length > 0 && (
        <div className="flex items-center gap-2">
          {socialMedia.map((sm: any) => (
            <a
              key={sm.platform}
              href={sm.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
              title={`@${sm.handle}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Share2 className="w-3 h-3" />
              <span>{SOCIAL_LABEL[sm.platform] || sm.platform}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Staff Cell - count display
// =============================================================================

export const StaffCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const count = row.activeStaffCount || row.active_staff_count || 0;
  if (!count) return <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">—</span>;
  return (
    <span className="flex items-center gap-1 text-sm text-text-secondary dark:text-text-dark-secondary">
      <Users className="w-3.5 h-3.5" />
      {count}
    </span>
  );
};

// =============================================================================
// Actions Cell - configurable action buttons via context
// =============================================================================

interface ActionsDef {
  id: string;
  icon: React.FC<{ className?: string }>;
  title: string;
  className: string;
  activeClassName?: string;
  isActive?: (row: any) => boolean;
  isHidden?: (row: any) => boolean;
  onClick: (row: any) => void;
  fill?: (row: any) => string;
}

export const ActionsCellRenderer: React.FC<ICellRendererParams> = (props) => {
  const row = props.data;
  if (!row) return null;
  const ctx = props.context || {};
  const actions: ActionsDef[] = ctx.actions || [];

  const visibleActions = actions.filter((a) => !a.isHidden?.(row));
  if (visibleActions.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5">
      {visibleActions.map((action) => {
        const isActive = action.isActive?.(row);
        const Icon = action.icon;
        return (
          <button
            key={action.id}
            onClick={(e) => { e.stopPropagation(); action.onClick(row); }}
            className={`p-1 transition-colors ${isActive ? (action.activeClassName || action.className) : action.className}`}
            title={action.title}
            aria-label={action.title}
          >
            <Icon className="w-4 h-4" />
          </button>
        );
      })}
    </div>
  );
};
